import { useEffect, useRef } from 'react'
import { Navigate } from 'react-router-dom'
import { toast } from 'sonner'

import { useAppTabs } from '@/hooks/useAppTabs'
import { Spinner } from '@/components/ui/spinner'
import type { ReactNode } from 'react'

interface ProtectedRouteProps {
  /**
   * Whether the caller may see the page, or undefined while that is still
   * being resolved. Waiting beats bouncing someone who turns out to be
   * entitled, so an unresolved verdict renders the spinner rather than denying.
   */
  allowed: boolean | undefined
  /** Shown once on denial. */
  message: string
  /** Toast id, so a remount does not stack duplicates. */
  toastId: string
  children: ReactNode
}

/**
 * The shared shape of every route guard: wait, then either render the page or
 * bounce with an explanation.
 *
 * On denial it evicts the tab the user arrived on before redirecting —
 * otherwise the tab survives pointing at a page they cannot open, and clicking
 * it bounces them again. Eviction, not a close request: the user has no say
 * here, so there is nothing to prompt about. An unsaved draft on a page they
 * are not entitled to could never be saved anyway.
 */
export function ProtectedRoute({
  allowed,
  message,
  toastId,
  children,
}: ProtectedRouteProps) {
  const { activeTab, evictTab } = useAppTabs()
  const initialTabIdRef = useRef(activeTab?.id)
  const denied = allowed === false

  useEffect(() => {
    if (denied) {
      toast.error(message, { id: toastId, duration: 4000 })
      if (initialTabIdRef.current) evictTab(initialTabIdRef.current, message)
    }
  }, [denied, message, toastId, evictTab])

  if (allowed === undefined) return <Spinner className="m-auto mt-32 h-8 w-8" />
  if (denied) return <Navigate to="/" replace />

  return <>{children}</>
}
